import { useState } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Row, Col, Badge, Button, Modal, Form, Alert } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader.jsx';
import { fmtMoney } from '../lib/format.js';
import { groupFeaturesIntoBundles } from '../lib/featureBundles.js';

function renderFeatureItem(f, { isRevoked, openFeatureEdit, deleteFeature }) {
  return (
    <div key={f.feature_definition_id} className="d-flex justify-content-between align-items-start border-bottom py-2 gap-3">
      <div className="flex-grow-1">
        <div className="d-flex align-items-center gap-2 mb-1">
          <span className="fw-semibold">{f.feature_name}</span>
          <code className="small text-muted">{f.feature_key}</code>
          <Badge bg="" className="badge-soft-secondary">{f.feature_type}</Badge>
        </div>
        <pre className="small mb-0 text-muted">{JSON.stringify(f.value, null, 2)}</pre>
      </div>
      {!isRevoked && (
        <div className="d-flex gap-1 flex-shrink-0">
          <Button size="sm" variant="outline-secondary" title="Werte bearbeiten"
                  onClick={() => openFeatureEdit(f)}>
            <Icon icon="tabler:edit" />
          </Button>
          <Button size="sm" variant="outline-danger" title="Feature entfernen"
                  onClick={() => {
                    if (confirm(`Feature "${f.feature_name}" aus dieser Lizenz entfernen?`)) {
                      deleteFeature.mutate(f.feature_key);
                    }
                  }}>
            <Icon icon="tabler:trash" />
          </Button>
        </div>
      )}
    </div>
  );
}

function BundleSection({ bundle, isRevoked, openFeatureEdit, deleteFeature }) {
  // Children werden auto-expanded wenn der Master "enabled" ist — sonst
  // standardmaessig zugeklappt mit Accordion-Toggle. Lokaler State erlaubt
  // dem Operator manuelles Aufklappen auch bei enabled=false (z.B. um die
  // Quotas zu inspizieren).
  const masterEnabled = bundle.master.value?.enabled === true;
  const [expanded, setExpanded] = useState(masterEnabled);
  const showChildren = expanded && bundle.children.length > 0;
  return (
    <div className="border rounded-2 p-2">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div className="text-muted small text-uppercase" style={{ letterSpacing: '0.04em' }}>
          <Icon icon="tabler:package" className="me-1" />{bundle.master.feature_name}
        </div>
        {bundle.children.length > 0 && (
          <Button size="sm" variant="link" className="p-0 text-decoration-none"
                  onClick={() => setExpanded(v => !v)}>
            <Icon icon={expanded ? 'tabler:chevron-up' : 'tabler:chevron-down'} className="me-1" />
            {expanded
              ? 'Bundle-Features ausblenden'
              : `Bundle-Features anzeigen (${bundle.children.length})`}
          </Button>
        )}
      </div>
      {renderFeatureItem(bundle.master, { isRevoked, openFeatureEdit, deleteFeature })}
      {showChildren && (
        <div className="ms-3 mt-1">
          {bundle.children.map(c => renderFeatureItem(c, { isRevoked, openFeatureEdit, deleteFeature }))}
        </div>
      )}
    </div>
  );
}

export default function LicenseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [showRevoke, setShowRevoke] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [configuratorBanner, setConfiguratorBanner] = useState(location.state?.configuratorResult || null);

  // Feature-Edit-Modal: pro Lizenz lassen sich die Werte einzelner Features
  // pflegen (z.B. URLs in streaming.edge ueberschreiben). Backend-Endpoint
  // ist PUT /licenses/:id/features/:feature_key mit body {value: {...}}.
  const [editingFeature, setEditingFeature] = useState(null); // {feature_key, feature_name, value}
  const [draftValue, setDraftValue] = useState('');
  const [draftError, setDraftError] = useState(null);

  const lic = useQuery({
    queryKey: ['license', id],
    queryFn: () => api.get(`/licenses/${id}`).then(r => r.data)
  });

  const revoke = useMutation({
    mutationFn: () => api.post(`/licenses/${id}/revoke`, { reason: reason || null }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['license', id] });
      qc.invalidateQueries({ queryKey: ['licenses-all'] });
      setShowRevoke(false);
    },
    onError: (e) => setError(e.response?.data?.message || e.message)
  });

  const unrevoke = useMutation({
    mutationFn: () => api.post(`/licenses/${id}/unrevoke`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['license', id] });
      qc.invalidateQueries({ queryKey: ['licenses-all'] });
    },
    onError: (e) => setError(e.response?.data?.message || e.message)
  });

  // FP-Unbind: setzt bound_fp zurueck damit License erneut auf einer Box
  // gebunden werden kann. Use-cases: HW-Defekt / Edge-Tausch / Re-Install /
  // license.json verloren. Audit-Log behaelt den vorherigen FP.
  const unbind = useMutation({
    mutationFn: (reason) => api.post(`/licenses/${id}/unbind`, { reason: reason || null }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['license', id] });
      qc.invalidateQueries({ queryKey: ['licenses-all'] });
    },
    onError: (e) => setError(e.response?.data?.message || e.message)
  });

  const del = useMutation({
    mutationFn: () => api.delete(`/licenses/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['licenses-all'] });
      navigate('/licenses');
    },
    onError: (e) => alert(e.response?.data?.message || e.message)
  });

  const updateFeatureValue = useMutation({
    mutationFn: ({ feature_key, value }) =>
      api.put(`/licenses/${id}/features/${feature_key}`, { value }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['license', id] });
      setEditingFeature(null);
    },
    onError: (e) => setDraftError(e.response?.data?.message || e.message)
  });

  const deleteFeature = useMutation({
    mutationFn: (feature_key) =>
      api.delete(`/licenses/${id}/features/${feature_key}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['license', id] });
    },
    onError: (e) => alert(e.response?.data?.message || e.message)
  });

  // license.json downloaden — Backend rendert das File mit frisch gesigntem
  // bootstrap_token. Customer legt es nach <ConfigDir>/license.json und der
  // Edge bootet damit (KontorURL + MQTT-URL kommen aus dem token-Claim).
  async function downloadLicenseFile(licenseId) {
    try {
      const r = await api.get(`/licenses/${licenseId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'license.json';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Download fehlgeschlagen: ' + (e.response?.data?.error || e.message));
    }
  }

  function openFeatureEdit(f) {
    setEditingFeature({ feature_key: f.feature_key, feature_name: f.feature_name });
    setDraftValue(JSON.stringify(f.value, null, 2));
    setDraftError(null);
  }

  function saveFeature() {
    setDraftError(null);
    let parsed;
    try {
      parsed = JSON.parse(draftValue);
    } catch (e) {
      setDraftError('Ungueltiges JSON: ' + e.message);
      return;
    }
    if (typeof parsed !== 'object' || Array.isArray(parsed) || parsed === null) {
      setDraftError('Wert muss ein Objekt sein.');
      return;
    }
    updateFeatureValue.mutate({ feature_key: editingFeature.feature_key, value: parsed });
  }

  if (lic.isLoading) return <div>Lade…</div>;
  if (lic.error) return <Alert variant="danger">Fehler beim Laden</Alert>;

  const license = lic.data.license;
  const features = lic.data.features;
  const isRevoked = !!license.revoked_at;
  const isDownloaded = isRevoked && license.license_kind === 'download'
    && license.revoke_reason === 'auto_revoke_download';

  // Bundle-Sicht: Master ist Feature mit '.enabled'-Suffix, Children teilen
  // den Namespace-Prefix. Wir mappen feature_key→key fuer den Helper.
  const { bundles, unbundled } = groupFeaturesIntoBundles(
    features.map(f => ({ ...f, key: f.feature_key }))
  );

  return (
    <>
      {configuratorBanner && (
        <Alert variant="success" className="d-flex align-items-start gap-2"
               dismissible onClose={() => setConfiguratorBanner(null)}>
          <Icon icon="tabler:check-circle" style={{ fontSize: 24, flexShrink: 0 }} />
          <div className="flex-grow-1">
            <strong>Lizenz erfolgreich angelegt.</strong>
            {configuratorBanner.invoice && (
              <> Rechnung <Link to={`/invoices/${configuratorBanner.invoice.id}`}>
                {configuratorBanner.invoice.invoice_number || `Entwurf #${configuratorBanner.invoice.id.slice(0,8)}`}
              </Link> wurde {configuratorBanner.invoice.status === 'draft' ? 'als Entwurf' : 'ausgestellt'} mit
              Gesamtsumme <strong>{fmtMoney(configuratorBanner.invoice.total_cents)}</strong>.</>
            )}
            {!configuratorBanner.invoice && configuratorBanner.pricing?.length > 0 && (
              <> Es wurde keine Rechnung erstellt (alle Features kostenlos oder Option deaktiviert).</>
            )}
            {configuratorBanner.token && (
              <div className="mt-2">
                <Button size="sm" variant="primary" onClick={() => downloadLicenseFile(license.id)}>
                  <Icon icon="tabler:download" className="me-1" />license.json herunterladen
                </Button>
                <Form.Text className="d-block mt-1">
                  Kunde legt die Datei nach <code>C:\ProgramData\uav-bos\license.json</code> (Windows)
                  bzw. <code>/etc/uav-bos/license.json</code> (Linux) und startet streaming-edge neu.
                </Form.Text>
              </div>
            )}
          </div>
        </Alert>
      )}

      <PageHeader
        title={license.name}
        subtitle={<>jti: <code>{license.jti}</code></>}
        actions={
          <>
            <Link to="/licenses" className="btn btn-outline-secondary"><Icon icon="tabler:arrow-left" className="me-1" />Zurück</Link>
            {!isRevoked && license.license_kind !== 'api' && (
              <Button variant="outline-primary" onClick={() => downloadLicenseFile(license.id)} title="license.json fuer Edge-Box herunterladen">
                <Icon icon="tabler:download" className="me-1" />license.json
              </Button>
            )}
            {!isRevoked && license.bound_fp && (
              <Button variant="outline-warning"
                onClick={() => {
                  const reason = prompt('Grund fuer FP-Unbind (z.B. Edge-Tausch, HW-Defekt, license.json verloren):');
                  if (reason !== null) unbind.mutate(reason);
                }}
                disabled={unbind.isLoading}
                title="TOFU-Binding aufheben damit License auf neuer Box erneut runtergeladen + gebunden werden kann">
                <Icon icon="tabler:link-off" className="me-1" />
                {unbind.isLoading ? 'Unbinde…' : 'FP-Unbind'}
              </Button>
            )}
            {!isRevoked && (
              <Button variant="outline-danger" onClick={() => setShowRevoke(true)}>
                <Icon icon="tabler:circle-x" className="me-1" />Widerrufen
              </Button>
            )}
            {isRevoked && (
              <>
                <Button variant="outline-success"
                  onClick={() => confirm('Lizenz reaktivieren? Refresh-Anfragen vom Edge gehen sofort wieder durch.') && unrevoke.mutate()}
                  disabled={unrevoke.isLoading}>
                  <Icon icon="tabler:refresh" className="me-1" />
                  {unrevoke.isLoading ? 'Reaktiviere…' : 'Reaktivieren'}
                </Button>
                <Button variant="outline-danger"
                  onClick={() => confirm('Lizenz unwiderruflich löschen? Audit-Eintrag bleibt erhalten.') && del.mutate()}
                  disabled={del.isLoading}>
                  <Icon icon="tabler:trash" className="me-1" />Endgültig löschen
                </Button>
              </>
            )}
          </>
        }
      />

      {isDownloaded && (
        <Alert variant="info">
          <Icon icon="tabler:download" className="me-2" />
          <strong>Einmalig heruntergeladen am {new Date(license.revoked_at).toLocaleString('de-DE')}.</strong>
          {' '}Diese Download-Lizenz wurde nach dem ersten Binden automatisch gesperrt (Einzel-Box-Schutz).
          Mit <em>Reaktivieren</em> laesst sich die Bindung aufheben und die Lizenz neu ausliefern.
        </Alert>
      )}
      {isRevoked && !isDownloaded && (
        <Alert variant="danger">
          <Icon icon="tabler:shield-off" className="me-2" />
          <strong>Widerrufen am {new Date(license.revoked_at).toLocaleString('de-DE')}.</strong>
          {license.revoke_reason && <span> Grund: {license.revoke_reason}</span>}
        </Alert>
      )}

      <Row className="g-3 mb-3">
        <Col md={4}>
          <Card className="card-flat h-100">
            <Card.Body>
              <div className="text-muted small text-uppercase mb-2">Status</div>
              {isDownloaded
                ? <Badge bg="" className="badge-soft-info">Heruntergeladen</Badge>
                : isRevoked
                  ? <Badge bg="" className="badge-soft-danger">Widerrufen</Badge>
                  : <Badge bg="" className="badge-soft-success">Aktiv</Badge>
              }
              <div className="mt-3 small">
                <div><span className="text-muted">Gültig ab:</span> {new Date(license.valid_from).toLocaleString('de-DE')}</div>
                <div><span className="text-muted">Gültig bis:</span> {license.valid_until ? new Date(license.valid_until).toLocaleString('de-DE') : '—'}</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="card-flat h-100">
            <Card.Body>
              <div className="text-muted small text-uppercase mb-2">Plan</div>
              <div>{license.plan_label || <span className="text-muted">—</span>}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="card-flat h-100">
            <Card.Body>
              <div className="text-muted small text-uppercase mb-2">Erstellt</div>
              <div>{new Date(license.created_at).toLocaleString('de-DE')}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="card-flat">
        <Card.Header className="border-0 pt-3"><h5 className="mb-0">Features</h5></Card.Header>
        <Card.Body>
          {features.length === 0 ? (
            <div className="text-muted">Keine Features in dieser Lizenz.</div>
          ) : (
            <div className="d-flex flex-column gap-3">
              {bundles.map(b => (
                <BundleSection key={b.master.feature_definition_id}
                               bundle={b} isRevoked={isRevoked}
                               openFeatureEdit={openFeatureEdit} deleteFeature={deleteFeature} />
              ))}
              {unbundled.length > 0 && (
                <div className="d-flex flex-column gap-2">
                  {unbundled.map(f => renderFeatureItem(f, { isRevoked, openFeatureEdit, deleteFeature }))}
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!editingFeature} onHide={() => setEditingFeature(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Feature-Werte bearbeiten: <code>{editingFeature?.feature_key}</code>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {draftError && <Alert variant="danger" className="py-2 small">{draftError}</Alert>}
          <Form.Group>
            <Form.Label className="small">Wert (JSON, gegen das Feature-Schema validiert)</Form.Label>
            <Form.Control as="textarea" rows={10} className="font-monospace small"
                          value={draftValue}
                          onChange={(e) => setDraftValue(e.target.value)} />
            <Form.Text>
              Das Backend validiert den Wert gegen das JSON-Schema des Features.
              Aenderungen sind sofort wirksam — bestehende Tokens werden beim
              naechsten Refresh die neuen Werte ausliefern.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setEditingFeature(null)}>Abbrechen</Button>
          <Button onClick={saveFeature} disabled={updateFeatureValue.isLoading}>
            {updateFeatureValue.isLoading ? 'Speichere…' : 'Speichern'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showRevoke} onHide={() => setShowRevoke(false)} centered>
        <Modal.Header closeButton><Modal.Title>Lizenz widerrufen</Modal.Title></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
          <p>Diese Aktion ist <strong>unmittelbar wirksam</strong>. Konsumenten der Lizenz verlieren beim nächsten Refresh-Versuch den Zugriff.</p>
          <Form.Group>
            <Form.Label>Grund (optional)</Form.Label>
            <Form.Control as="textarea" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowRevoke(false)}>Abbrechen</Button>
          <Button variant="danger" onClick={() => revoke.mutate()} disabled={revoke.isLoading}>
            {revoke.isLoading ? 'Widerrufen…' : 'Endgültig widerrufen'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
